"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useBtcPrice } from "@/hooks/useBtcPrice";
import {
	convertSatsToUsd,
	convertUsdToSats,
} from "@/lib/utils/platform-prices";
import {
	fetch2RFITokenInfo,
	fetchBoundPrice,
	fetchFomoWellPrice,
	fetchIcpSwapPriceUsd,
	fetchOkxWeb3PriceUsd,
	fetchRichSwapPrice,
	type PlatformId,
	type PlatformInfo,
} from "@/lib/utils/token-api";

export type PlatformLoadStatus = "loading" | "ready" | "error";

export interface PlatformDisplayInfo extends PlatformInfo {
	status: PlatformLoadStatus;
	isRefreshing: boolean;
	satsPriceLabel: string;
}

type PlatformState = Omit<
	PlatformDisplayInfo,
	"price" | "priceUsd" | "satsPriceLabel"
> & {
	price: number | null;
	priceUsd?: number | null;
};

const INITIAL_PLATFORM_STATES: PlatformState[] = [
	{
		id: "odinfun",
		name: "OdinFun",
		buyLink: "https://www.odin.baby/plaza/token/2rfi",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/odinfun.png",
		price: null,
		priceUsd: null,
		status: "loading",
		isLoading: true,
		error: undefined,
		isRefreshing: false,
	},
	{
		id: "fomowell",
		name: "FomoWell",
		buyLink: "https://www.fomowell.baby/plaza/token/66",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/fomowell.png",
		price: null,
		priceUsd: null,
		status: "loading",
		isLoading: true,
		error: undefined,
		isRefreshing: false,
	},
	{
		id: "richswap",
		name: "RichSwap",
		buyLink:
			"https://www.richswap.io/swap?coinA=BTC&coinB=BABYODIN%E2%80%A2ID%E2%80%A2NWKQ%E2%80%A2ODIN",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/richswap.png",
		price: null,
		priceUsd: null,
		status: "loading",
		isLoading: true,
		error: undefined,
		isRefreshing: false,
	},
	{
		id: "icpswap",
		name: "IcpSwap",
		buyLink:
			"https://app.icpswap.com/info-swap/token/details/n4v6w-viaaa-aaaar-qbrvq-cai",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/icpswap.png",
		price: null,
		priceUsd: null,
		status: "loading",
		isLoading: true,
		error: undefined,
		isRefreshing: false,
	},
	{
		id: "okxweb3",
		name: "OKX Web3",
		buyLink:
			"https://web3.okx.com/zh-hans/token/ethereum/0xc5d8bc1eefbfbf354ac962f3e3bcf0b618198bb1",
		priceUnit: "USD",
		logoSrc: "/swaplogo/okx-web3.png",
		price: null,
		priceUsd: null,
		status: "loading",
		isLoading: true,
		error: undefined,
		isRefreshing: false,
	},
	{
		id: "bound",
		name: "Bound",
		buyLink:
			"https://app.bound.exchange/trade?inputCurrency=905743:1874&outputCurrency=0:0",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/bound.svg",
		price: null,
		priceUsd: null,
		status: "loading",
		isLoading: true,
		error: undefined,
		isRefreshing: false,
	},
];

function updatePlatformState(
	prev: PlatformState[],
	platformId: PlatformId,
	patch: Partial<PlatformState>,
): PlatformState[] {
	return prev.map((platform) =>
		platform.id === platformId ? { ...platform, ...patch } : platform,
	);
}

function formatSatsPriceLabel(price: number | null | undefined): string {
	if (typeof price !== "number" || !Number.isFinite(price) || price <= 0) {
		return "-- Sats";
	}

	return `${price.toLocaleString("en-US", {
		minimumFractionDigits: 3,
		maximumFractionDigits: 3,
	})} Sats`;
}

function toDisplayInfo(
	platform: PlatformState & { satsPriceLabel: string },
): PlatformDisplayInfo {
	return {
		...platform,
		isLoading: platform.status === "loading",
	};
}

export function useBabyOdinPlatformInfo() {
	const { btcPrice } = useBtcPrice();
	const [platformStates, setPlatformStates] = useState<PlatformState[]>(
		INITIAL_PLATFORM_STATES,
	);
	const btcPriceRef = useRef(btcPrice);

	useEffect(() => {
		btcPriceRef.current = btcPrice;
	}, [btcPrice]);

	useEffect(() => {
		let cancelled = false;

		const markRefreshing = () => {
			setPlatformStates((prev) =>
				prev.map((platform) => ({
					...platform,
					isRefreshing: platform.status === "ready",
					error: undefined,
				})),
			);
		};

		const loadOdinFun = async () => {
			try {
				const tokenInfo = await fetch2RFITokenInfo();
				if (cancelled) return;

				setPlatformStates((prev) =>
					updatePlatformState(prev, "odinfun", {
						price:
							tokenInfo.price != null ? Number(tokenInfo.price) / 1000 : null,
						priceUsd: null,
						status: "ready",
						error: undefined,
						isRefreshing: false,
					}),
				);
			} catch (error) {
				if (cancelled) return;
				setPlatformStates((prev) =>
					updatePlatformState(prev, "odinfun", {
						status:
							prev.find((platform) => platform.id === "odinfun")?.price != null
								? "ready"
								: "error",
						error:
							error instanceof Error ? error.message : "Failed to fetch price",
						isRefreshing: false,
					}),
				);
			}
		};

		const loadFomoWell = async () => {
			try {
				const price = await fetchFomoWellPrice();
				if (cancelled) return;

				setPlatformStates((prev) =>
					updatePlatformState(prev, "fomowell", {
						price,
						priceUsd: null,
						status: "ready",
						error: undefined,
						isRefreshing: false,
					}),
				);
			} catch (error) {
				if (cancelled) return;
				setPlatformStates((prev) =>
					updatePlatformState(prev, "fomowell", {
						status:
							prev.find((platform) => platform.id === "fomowell")?.price != null
								? "ready"
								: "error",
						error:
							error instanceof Error ? error.message : "Failed to fetch price",
						isRefreshing: false,
					}),
				);
			}
		};

		const loadRichSwap = async () => {
			try {
				const price = await fetchRichSwapPrice();
				if (cancelled) return;

				setPlatformStates((prev) =>
					updatePlatformState(prev, "richswap", {
						price,
						priceUsd: null,
						status: "ready",
						error: undefined,
						isRefreshing: false,
					}),
				);
			} catch (error) {
				if (cancelled) return;
				setPlatformStates((prev) =>
					updatePlatformState(prev, "richswap", {
						status:
							prev.find((platform) => platform.id === "richswap")?.price != null
								? "ready"
								: "error",
						error:
							error instanceof Error ? error.message : "Failed to fetch price",
						isRefreshing: false,
					}),
				);
			}
		};

		const loadIcpSwap = async () => {
			try {
				const priceUsd = await fetchIcpSwapPriceUsd();
				if (cancelled) return;

				setPlatformStates((prev) =>
					updatePlatformState(prev, "icpswap", {
						priceUsd,
						price: convertUsdToSats(priceUsd, btcPriceRef.current),
						status: "ready",
						error: undefined,
						isRefreshing: false,
					}),
				);
			} catch (error) {
				if (cancelled) return;
				setPlatformStates((prev) =>
					updatePlatformState(prev, "icpswap", {
						status:
							prev.find((platform) => platform.id === "icpswap")?.priceUsd !=
							null
								? "ready"
								: "error",
						error:
							error instanceof Error ? error.message : "Failed to fetch price",
						isRefreshing: false,
					}),
				);
			}
		};

		const loadOkxWeb3 = async () => {
			try {
				const priceUsd = await fetchOkxWeb3PriceUsd();
				if (cancelled) return;

				setPlatformStates((prev) =>
					updatePlatformState(prev, "okxweb3", {
						priceUsd,
						price: convertUsdToSats(priceUsd, btcPriceRef.current),
						status: "ready",
						error: undefined,
						isRefreshing: false,
					}),
				);
			} catch (error) {
				if (cancelled) return;
				setPlatformStates((prev) =>
					updatePlatformState(prev, "okxweb3", {
						status:
							prev.find((platform) => platform.id === "okxweb3")?.priceUsd !=
							null
								? "ready"
								: "error",
						error:
							error instanceof Error ? error.message : "Failed to fetch price",
						isRefreshing: false,
					}),
				);
			}
		};

		const loadBound = async () => {
			try {
				const boundPrice = await fetchBoundPrice();
				if (!boundPrice) {
					throw new Error("Invalid Bound price response");
				}
				if (cancelled) return;

				setPlatformStates((prev) =>
					updatePlatformState(prev, "bound", {
						price: boundPrice.priceInSats,
						priceUsd: boundPrice.priceUsd,
						status: "ready",
						error: undefined,
						isRefreshing: false,
					}),
				);
			} catch (error) {
				if (cancelled) return;
				setPlatformStates((prev) =>
					updatePlatformState(prev, "bound", {
						status:
							prev.find((platform) => platform.id === "bound")?.price != null ||
							prev.find((platform) => platform.id === "bound")?.priceUsd != null
								? "ready"
								: "error",
						error:
							error instanceof Error ? error.message : "Failed to fetch price",
						isRefreshing: false,
					}),
				);
			}
		};

		const loadAll = () => {
			markRefreshing();
			void loadOdinFun();
			void loadFomoWell();
			void loadRichSwap();
			void loadIcpSwap();
			void loadOkxWeb3();
			void loadBound();
		};

		loadAll();
		const intervalId = setInterval(loadAll, 60000);

		return () => {
			cancelled = true;
			clearInterval(intervalId);
		};
	}, []);

	const platforms = useMemo(() => {
		return platformStates.map((platform) => {
			const resolvedPrice =
				platform.priceUsd != null
					? (convertUsdToSats(platform.priceUsd, btcPrice) ?? platform.price)
					: platform.price;
			const resolvedPriceUsd =
				platform.priceUsd ?? convertSatsToUsd(platform.price, btcPrice);

			return toDisplayInfo({
				...platform,
				price: resolvedPrice,
				priceUsd: resolvedPriceUsd,
				satsPriceLabel: formatSatsPriceLabel(resolvedPrice),
			});
		});
	}, [btcPrice, platformStates]);

	return {
		platforms,
	};
}
